import { useState } from 'react';
import { Table, Tag, Button, Typography, Card, Descriptions } from 'antd';
import { ReloadOutlined, AuditOutlined } from '@ant-design/icons';
import endpoints from '../../api/endpoints';
import { useQuery } from '../../hooks/useApi';
import { formatDateTime, shortId } from '../../utils/format';
import ErrorAlert from '../../components/ErrorAlert';
import LoadingBlock from '../../components/LoadingBlock';
import PageHeader from '../../components/PageHeader';
import StatusBadge from '../../components/StatusBadge';

const { Text } = Typography;

const EVENT_COLORS = {
  session_created: 'green',
  session_refreshed: 'blue',
  session_replaced: 'gold',
  session_logged_out: 'default',
  session_expired: 'orange',
  token_reuse_detected: 'red',
  suspicious_activity: 'red'
};

export default function SessionDebugPage() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const sessionQ = useQuery(() => endpoints.auth.session.current(), []);
  const eventsQ = useQuery(
    () => endpoints.auth.session.events({ page, pageSize }),
    [page, pageSize]
  );

  const events = (eventsQ.data && eventsQ.data.items) || [];

  const eventColumns = [
    {
      title: 'When',
      dataIndex: 'createdAt',
      render: (v) => (
        <span className="text-ink-700 text-sm">{formatDateTime(v)}</span>
      ),
      width: 200
    },
    {
      title: 'Event',
      dataIndex: 'eventType',
      render: (v) => <Tag color={EVENT_COLORS[v] || 'default'}>{v}</Tag>
    },
    {
      title: 'Session',
      dataIndex: 'sessionId',
      render: (v) => <code className="text-xs text-ink-500">{shortId(v)}</code>
    },
    {
      title: 'IP',
      dataIndex: 'ipAddress',
      render: (v) => <span className="text-ink-700 text-sm">{v || '—'}</span>
    },
    {
      title: 'User-Agent',
      dataIndex: 'userAgent',
      render: (v) => (
        <Text
          style={{ maxWidth: 240, display: 'inline-block' }}
          ellipsis
          title={v || ''}
        >
          <span className="text-ink-700 text-sm">{v || '—'}</span>
        </Text>
      )
    },
    {
      title: 'Metadata',
      dataIndex: 'metadata',
      render: (v) => {
        const keys = v && typeof v === 'object' ? Object.keys(v) : [];
        if (keys.length === 0) return <span className="text-ink-400">—</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {keys.map((k) => (
              <Tag key={k}>
                {k}={String(v[k]).slice(0, 24)}
              </Tag>
            ))}
          </div>
        );
      }
    }
  ];

  const sd = sessionQ.data;

  return (
    <div>
      <PageHeader
        title="Session"
        subtitle={
          <>
            Reads <code className="text-xs">GET /api/auth/session</code> and{' '}
            <code className="text-xs">GET /api/auth/session/events</code>. Own
            session and own events only — sub-users see only their own activity.
          </>
        }
        icon={<AuditOutlined />}
        actions={
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              sessionQ.refetch();
              eventsQ.refetch();
            }}
          >
            Refresh
          </Button>
        }
      />

      {/* Current session */}
      <Card title="Current session" size="small" className="mb-6">
        <ErrorAlert error={sessionQ.error} />
        {sessionQ.loading ? (
          <LoadingBlock height={120} />
        ) : sd ? (
          <Descriptions
            column={{ xs: 1, md: 2 }}
            size="small"
            colon={false}
          >
            <Descriptions.Item label="Session id">
              <code className="text-xs text-ink-700">{sd.id}</code>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              {sd.status === 'active' ? (
                <StatusBadge tone="bg-emerald-50 text-emerald-700 border-emerald-200" label="active" />
              ) : (
                <StatusBadge status="inactive" label={sd.status} />
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Created">
              <span className="text-ink-900 font-medium">{formatDateTime(sd.createdAt)}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Last seen">
              <span className="text-ink-900 font-medium">{formatDateTime(sd.lastSeenAt)}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Idle expires at">
              <span className="text-ink-900 font-medium">{formatDateTime(sd.expiresAt)}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Refresh expires at">
              <span className="text-ink-900 font-medium">{formatDateTime(sd.refreshTokenExpiresAt)}</span>
            </Descriptions.Item>
            <Descriptions.Item label="IP">
              <span className="text-ink-900 font-medium">{sd.ipAddress || '—'}</span>
            </Descriptions.Item>
            <Descriptions.Item label="UA">
              <Text style={{ maxWidth: 320 }} ellipsis>
                <span className="text-ink-700 text-sm">{sd.userAgent || '—'}</span>
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="User id">
              <code className="text-xs text-ink-700">{sd.userId}</code>
            </Descriptions.Item>
            <Descriptions.Item label="Role">
              <StatusBadge status={sd.isMaster ? 'master' : sd.role} />
            </Descriptions.Item>
          </Descriptions>
        ) : null}
      </Card>

      {/* Events */}
      <Card title="Events" size="small" styles={{ body: { padding: 0 } }}>
        <ErrorAlert error={eventsQ.error} style={{ margin: 16 }} />
        <Table
          size="small"
          rowKey="id"
          loading={eventsQ.loading}
          dataSource={events}
          columns={eventColumns}
          pagination={{
            current: page,
            pageSize,
            total: (eventsQ.data && eventsQ.data.total) || 0,
            showSizeChanger: true,
            onChange: (p, s) => {
              setPage(p);
              setPageSize(s);
            }
          }}
        />
      </Card>
    </div>
  );
}
